import { useCallback, useEffect, useRef, useState } from "react";
import { ImageBlockStatus, useGetImageGenerationJobStatusLazyQuery } from "@axio/graphql/generated/graphql";
import type { MessageItem } from "../containers/companion/companionMessage.types";

const POLL_MS = 3000;
const MAX_WAIT_MS = 5 * 60 * 1000;

export type CompletedImageGenerationJob = {
  id: string;
  url: string;
  description: string | null;
};

type JobStatusPayload = {
  status?: string | null;
  message?: string | null;
  result?: {
    imageUrl?: string | null;
    fileId?: string | null;
    description?: string | null;
  } | null;
};

const completedImageFromJobStatus = (payload: JobStatusPayload): CompletedImageGenerationJob | null => {
  const url = payload.result?.imageUrl;
  if (!url) return null;
  return {
    id: payload.result?.fileId ?? url,
    url,
    description: payload.result?.description ?? null,
  };
};

/** null = keep polling, "fail" = terminal failure, object = success (includes validation-failed jobs with an image) */
export const parseImageGenerationJobStatus = (
  payload: JobStatusPayload | null | undefined
): CompletedImageGenerationJob | "fail" | null => {
  if (!payload?.status || payload.status === "in_progress") return null;

  if (payload.status === "completed" || payload.status === "failed") {
    const image = completedImageFromJobStatus(payload);
    return image ?? "fail";
  }

  return "fail";
};

type ImagePollTarget = { streamChatKey: string; messageId: string; deadlineMs: number };
type SetMessagesByChatId = React.Dispatch<React.SetStateAction<Record<string, MessageItem[]>>>;

type PendingWait = {
  resolve: (result: CompletedImageGenerationJob | null) => void;
  deadlineMs: number;
  logPrefix: string;
};

/** ILE: await a job after mutation returns jobId. */
export function useImageGenerationJobPolling() {
  const [fetchStatus] = useGetImageGenerationJobStatusLazyQuery();
  const pendingRef = useRef<Record<string, PendingWait>>({});
  const [tick, setTick] = useState(0);

  const waitForJob = useCallback(
    (jobId: string, options?: { logPrefix?: string; maxWaitMs?: number }) =>
      new Promise<CompletedImageGenerationJob | null>((resolve) => {
        pendingRef.current[jobId] = {
          resolve,
          logPrefix: options?.logPrefix ?? "[ILE]",
          deadlineMs: Date.now() + (options?.maxWaitMs ?? MAX_WAIT_MS),
        };
        setTick((n) => n + 1);
      }),
    []
  );

  useEffect(() => {
    const finish = (jobId: string, result: CompletedImageGenerationJob | null) => {
      pendingRef.current[jobId]?.resolve(result);
      delete pendingRef.current[jobId];
      setTick((n) => n + 1);
    };

    const poll = async (jobId: string, pending: PendingWait) => {
      if (Date.now() > pending.deadlineMs) {
        console.error(`${pending.logPrefix} Image generation job timed out`, { jobId });
        finish(jobId, null);
        return;
      }
      try {
        const { data } = await fetchStatus({ variables: { jobId }, fetchPolicy: "network-only" });
        if (!pendingRef.current[jobId]) return;
        const parsed = parseImageGenerationJobStatus(data?.getImageGenerationJobStatus);
        if (parsed === null) return;
        if (parsed === "fail") {
          console.error(`${pending.logPrefix} Image generation job failed`, { jobId });
          finish(jobId, null);
          return;
        }
        if (data?.getImageGenerationJobStatus?.status === "failed") {
          console.warn(`${pending.logPrefix} Image generation validation failed; using generated image`, { jobId });
        }
        finish(jobId, parsed);
      } catch (error) {
        console.error(`${pending.logPrefix} Error polling image generation job`, { jobId, error });
      }
    };

    const pollAll = () => {
      for (const [jobId, pending] of Object.entries(pendingRef.current)) {
        void poll(jobId, pending);
      }
    };

    if (Object.keys(pendingRef.current).length === 0) return;
    pollAll();
    const interval = setInterval(pollAll, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus, tick]);

  return { waitForJob };
}

export function useImageGenerationPolling(setMessagesByChatId: SetMessagesByChatId) {
  const [fetchStatus] = useGetImageGenerationJobStatusLazyQuery();
  const targetsRef = useRef<Record<string, ImagePollTarget>>({});
  const [tick, setTick] = useState(0);

  const bump = () => setTick((n) => n + 1);

  const stopImageGenerationPolling = useCallback((jobId?: string) => {
    if (jobId) {
      if (!targetsRef.current[jobId]) return;
      const rest = { ...targetsRef.current };
      delete rest[jobId];
      targetsRef.current = rest;
      bump();
      return;
    }
    if (Object.keys(targetsRef.current).length === 0) return;
    targetsRef.current = {};
    bump();
  }, []);

  const stopImageGenerationPollingForMessage = useCallback((messageId: string) => {
    const next = Object.fromEntries(Object.entries(targetsRef.current).filter(([, t]) => t.messageId !== messageId));
    if (Object.keys(next).length === Object.keys(targetsRef.current).length) return;
    targetsRef.current = next;
    bump();
  }, []);

  const startImageGenerationPolling = useCallback((jobId: string, streamChatKey: string, messageId: string) => {
    const existing = targetsRef.current[jobId];
    if (existing && existing.streamChatKey === streamChatKey && existing.messageId === messageId) return; // already polling → no re-bump
    targetsRef.current = {
      ...targetsRef.current,
      [jobId]: { streamChatKey, messageId, deadlineMs: Date.now() + MAX_WAIT_MS },
    };
    bump();
  }, []);

  const remapImagePollMessageId = useCallback((oldMessageId: string, newMessageId: string) => {
    let changed = false;
    const next = Object.fromEntries(
      Object.entries(targetsRef.current).map(([jobId, target]) => {
        if (target.messageId !== oldMessageId) return [jobId, target];
        changed = true;
        return [jobId, { ...target, messageId: newMessageId }];
      })
    );
    if (changed) targetsRef.current = next;
  }, []);

  const remapImagePollChatKey = useCallback((oldChatKey: string, newChatKey: string) => {
    let changed = false;
    const next = Object.fromEntries(
      Object.entries(targetsRef.current).map(([jobId, target]) => {
        if (target.streamChatKey !== oldChatKey) return [jobId, target];
        changed = true;
        return [jobId, { ...target, streamChatKey: newChatKey }];
      })
    );
    if (changed) targetsRef.current = next;
  }, []);

  useEffect(() => {
    const setBlock = (target: ImagePollTarget, jobId: string, status: ImageBlockStatus, url: string | null) => {
      setMessagesByChatId((prev) => ({
        ...prev,
        [target.streamChatKey]: (prev[target.streamChatKey] ?? []).map((m) => {
          if (m.id !== target.messageId) return m;
          const existing = m.imageBlocks?.[0];
          return {
            ...m,
            imageBlocks: [
              {
                id: existing?.id ?? `temp-image-block-${jobId}`,
                status,
                jobId,
                url,
                errorMessage: null,
              },
            ],
          };
        }),
      }));
    };

    const poll = async (jobId: string, target: ImagePollTarget) => {
      if (Date.now() > target.deadlineMs) {
        console.warn("[useImageGenerationPolling] Image generation job timed out", { jobId });
        setBlock(target, jobId, ImageBlockStatus.Failed, null);
        stopImageGenerationPolling(jobId);
        return;
      }
      try {
        const { data } = await fetchStatus({ variables: { jobId }, fetchPolicy: "network-only" });
        if (!targetsRef.current[jobId]) return;
        const parsed = parseImageGenerationJobStatus(data?.getImageGenerationJobStatus);
        if (parsed === null) return;
        if (parsed === "fail") {
          console.error("[useImageGenerationPolling] Image generation job failed", { jobId });
          setBlock(target, jobId, ImageBlockStatus.Failed, null);
        } else {
          if (data?.getImageGenerationJobStatus?.status === "failed") {
            console.warn("[useImageGenerationPolling] Validation failed; using generated image", { jobId });
          }
          setBlock(target, jobId, ImageBlockStatus.Completed, parsed.url);
        }
        stopImageGenerationPolling(jobId);
      } catch (error) {
        console.error("[useImageGenerationPolling] Error polling image generation job", { jobId, error });
        setBlock(target, jobId, ImageBlockStatus.Failed, null);
        stopImageGenerationPolling(jobId);
      }
    };

    const pollAll = () => {
      for (const [jobId, target] of Object.entries(targetsRef.current)) {
        void poll(jobId, target);
      }
    };

    if (Object.keys(targetsRef.current).length === 0) return;
    pollAll();
    const interval = setInterval(pollAll, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus, tick, setMessagesByChatId, stopImageGenerationPolling]);

  return {
    startImageGenerationPolling,
    stopImageGenerationPolling,
    stopImageGenerationPollingForMessage,
    remapImagePollMessageId,
    remapImagePollChatKey,
  };
}
